import { css } from '@microsoft/fast-element';
import { focusStrokeOuter, focusStrokeThickness, strokeThickness } from '@adaptive-web/adaptive-ui/reference';

import type { Carousel } from './carousel.js';
import { MatchMediaStyleSheetBehavior, PropertyStyleSheetBehavior } from '@microsoft/fast-foundation';
import { darkModeQuery } from '../../style.js';

export default css<Carousel>/* css */`
	:host {
		--item-width: 660px;
		--item-height: 400px;

		display: grid;
		grid-template-columns: 0.5fr min(var(--item-width), 100%) 0.5fr;
		grid-template-rows: calc(var(--item-height) + 4px) max-content;
		grid-auto-flow: column;
		height: calc(var(--item-height) + 22px);
		gap: var(--padding-md) 0;
		container: carousel / size;
		box-sizing: border-box;
		position: relative;
	}

	:is(.previous, .next) {
		opacity: 0;
		grid-column: 2;
		position: absolute;
		top: calc((var(--item-height) * 0.6) - 22px);
		
		transition: opacity 400ms ease;
		z-index: 2;

		color: #537494;
		background-color: var(--background-color);
		border-color: var(--surface-color);

		width: 48px;
		height: 48px;

		&:hover {
			color: var(--secondary-color);
			background-color: var(--primary-color);
			border-color: var(--primary-color);
		}
	}

	:is(.previous, .next)[hidden] {
		display: none;
	}

	:host(:hover) :is(.previous, .next) {
    opacity: 1;
  }

	.previous {
		left: -24px;
	}

	.next {
		left: calc(min(var(--item-width), 98cqi) - 24px);
	}

	:host::before,
	:host::after {
		display: flex;
		align-items: center;
		justify-content: center;
		position: absolute;
		top: 0;
		height: calc(var(--item-height) + 4px);
		width: min(100cqi - var(--item-width), 15cqi);

		transition: opacity 600ms ease;
		z-index: 1;
		box-sizing: border-box;
	}

	:host::before {
		content: '';
		opacity: ${x => x.fadeStart};
		left: 0;
		background: linear-gradient(to right, var(--background-color) 0%, transparent 100%);
	}

	:host::after {
		content: '';
		opacity: ${x => x.fadeEnd};
		right: 0;
		background: linear-gradient(to left, var(--background-color) 0%, transparent 100%);
	}

	.scroll-container {
		display: grid;
		grid-column: 1 / span all;
		grid-auto-flow: column;
		gap: var(--padding-lg);
		align-items: center;
		scroll-snap-type: x mandatory;
		container: scroll / inline-size;
		padding-block: 2px;
		overflow-y: hidden;
		overflow-x: overlay;
		contain: content;
		box-sizing: border-box;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	.scroll-container > .end-cap {
		width: min(var(--item-width), 100cqi);
		height: var(--item-height);
		background-color: transparent;
	}

	::slotted(*) {
		width: min(var(--item-width), 96cqi);
		height: var(--item-height);
		border: ${strokeThickness} solid transparent;

		scroll-snap-align: center;
		scroll-snap-stop: always;
	}

	.previous, .scroll-container, .next {
		grid-row: 1;
	}

	.pagination-container {
		display: flex;
		grid-column: 1 / span all;
		grid-row: 2;
		gap: var(--padding-xs);
		justify-self: center;
	}

	.pagination-container > button {
		width: 18px;
		height: 18px;
		padding: 2px;
		border-radius: min(50%, 999px);
		background-color: var(--primary-color);
		border: ${strokeThickness} solid var(--primary-color);
		cursor: pointer;

		&:focus {
			outline: ${focusStrokeThickness} solid ${focusStrokeOuter};
			outline-offset: 1px;
		}
	}

	.pagination-container > button[aria-selected="true"] {
		background-color: var(--secondary-color);
		border-color: transparent;
	}
`.withBehaviors(
	new PropertyStyleSheetBehavior(
		'viewMode',
		'mobile',
		css`
			.end-cap {
				display: none;
			}

			.previous {
				left: -12px;
			}

			.next {
				left: calc(min(var(--item-width), 98cqi) - 28px);
			}

			::slotted(:first-child) {
				margin-inline-start: 2cqi;
			}

			::slotted(:last-child) {
				margin-inline-end: 2cqi;
			}
		`
	),
	new MatchMediaStyleSheetBehavior(
		darkModeQuery,
		css`
			:is(.previous, .next) {
				background-color: var(--primary-color);
				border-color: var(--on-surface-color);
				color: var(--secondary-color);

				&:hover {
					background-color: #ffffff;
					border-color: transparent;
					color: var(--primary-color);
				}
			}

			.pagination-container > button {
				background-color: #bddeff;
				border-color: #bddeff;
			}

			::slotted(*) {
				border-color: var(--on-surface-color);
			}
		`
	)
);